import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { useLocation, useParams } from "react-router-dom";
import { getApi } from "../../../Services/apiService";
import CreateEventModal from "./CreateEventModal";
import DeleteEventModal from "./DeleteEventModal";
import { toast } from "react-toastify";
import {
  convertUTCToDateInvitation,
  convertUTCToTime12Hour,
} from "../../../Utils/dateUtils";
import { renderNameWithColor } from "../../../Utils/colors";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import { formatDashboardFamilyLineHeadName } from "../../../Utils/dashboardMemberDisplayName";
import { formatSuffix, getMemberSuffix } from "../../../Utils/name";
import SpecialInstructionsDisplay from "./SpecialInstructionsDisplay";
import { isFuneralEvent } from "./funeralFlyer";

const EventDetails = () => {
  const { id } = useParams();
  const query = new URLSearchParams(useLocation().search);
  const family_tree_id = query.get("family_tree_id");

  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nameSortOrder, setNameSortOrder] = useState("asc");

  // Modal view for the edit and the delete
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Extract fetch function so it can be reused
  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const family_tree_id_value = getFamilyTreeIdFromStorage() || family_tree_id;
      const response = await getApi(`event-details/${id}?family_tree_id=${family_tree_id_value}`);
      console.log(response);
      if (response?.statusCode === 200) {
        setEventData(response?.data);
        setError(null);
      }
    } catch (error) {
      console.error(error);
      setError("Error connecting to server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchEventDetails();
    }
  }, [id]);

  // Handle edit modal close - refresh data if update was successful
  const handleEditModalClose = (success) => {
    setShowEditModal(false);
    // If update was successful, refetch the event details
    if (success === true) {
      fetchEventDetails();
    }
  };

  // Helper function to render status badge based on integer value
  const renderStatusBadge = (status) => {
    // Assumption: 1 = Accepted, 2 = Declined, 0 = Pending
    // Adjust these case numbers based on your exact backend logic
    switch (status) {
      case 1:
        return <span className="status-accepted">Accepted</span>;
      case 2:
        return <span className="status-decline">Decline</span>;
      case 0:
      default:
        return <span className="status-pending">Pending</span>;
    }
  };

  // Helper function to format person name with suffix or special name (matching TotalFamilyMembers.jsx)
  const formatPersonName = (member) => {
    // Only use first_name, extract first word from name if first_name not available
    let firstName = member?.first_name || "";
    if (!firstName && member?.name) {
      // Extract first word from full name as fallback
      firstName = member.name.trim().split(" ")[0] || "Unnamed";
    }
    if (!firstName) {
      firstName = "Unnamed";
    }

    const suffix = getMemberSuffix(member);
    const specialName = member?.special_name || "";

    // Format name with suffix or special name
    if (suffix) {
      return `${firstName}, ${formatSuffix(suffix)}`;
    } else if (specialName) {
      return `${firstName} ${specialName}`;
    }
    return firstName;
  };

  const getHeadOfLineName = formatDashboardFamilyLineHeadName;

  const toggleNameSortOrder = () => {
    setNameSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const sortedInvitedMembers = React.useMemo(() => {
    const invitedMembers = eventData?.invited_members || [];
    if (!invitedMembers || invitedMembers.length === 0) return [];
    const membersCopy = [...invitedMembers];
    membersCopy.sort((a, b) => {
      const nameA = (formatPersonName(a) || "").toLowerCase();
      const nameB = (formatPersonName(b) || "").toLowerCase();
      if (nameA < nameB) return nameSortOrder === "asc" ? -1 : 1;
      if (nameA > nameB) return nameSortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return membersCopy;
  }, [eventData, nameSortOrder]);

  if (loading) {
    return <div>Loading event details...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }
  if (!eventData) return null;

  const { event, invited_members, total_invited, accepted_count, declined_count, pending_count } = eventData;
  const getGenerationLabel = (gen) => {
    if (!gen) return "N/A";

    const labels = {
      1: "Grandparent",
      2: "Parent",
      3: "Grandchildren",
      4: "Great Grandchildren",
    };

    if (labels[gen]) return labels[gen];

    // For gen >= 5:
    // Gen 5 → Great² Grandchildren (n-3)
    const n = gen - 3;

    return `Great${n > 1 ? `\u00B3`.repeat(n - 1) : ""} Grandchildren`;
  };

  // Helper function to get relation to top of the line based on generation and gender
  const getRelationToTopOfLine = (gen, gender) => {
    if (!gen) return "";

    // Gen 1: Self
    if (gen === 1) {
      return "Self";
    }

    // Gen 2: Son or Daughter
    if (gen === 2) {
      return gender === 1 ? "Son" : gender === 2 ? "Daughter" : "";
    }

    // Gen 3: Grandson or Granddaughter
    if (gen === 3) {
      return gender === 1 ? "Grandson" : gender === 2 ? "Granddaughter" : "";
    }

    // Gen 4: Gr Grandson or Gr Granddaughter
    if (gen === 4) {
      return gender === 1 ? "Gr Grandson" : gender === 2 ? "Gr Granddaughter" : "";
    }

    // Gen 5: Gr Gr Grandson or Gr Gr Granddaughter
    if (gen === 5) {
      return gender === 1 ? "Gr Gr Grandson" : gender === 2 ? "Gr Gr Granddaughter" : "";
    }

    // For gen >= 6, calculate the number of "Gr" prefixes
    if (gen >= 6) {
      const grCount = gen - 3; // Gen 6 = 3 "Gr", Gen 7 = 4 "Gr", etc.
      const grPrefix = "Gr ".repeat(grCount);
      return gender === 1 ? `${grPrefix}Grandson` : gender === 2 ? `${grPrefix}Granddaughter` : "";
    }

    return "";
  };

  const formatDateShort = convertUTCToDateInvitation;
  const formatTime12Hour = convertUTCToTime12Hour;

  return (
    <div className="family-tree-container">
      {/* ===== TOP CONTROLS ===== */}

      <div className="btn-header">
        <div></div>
        <div className="button-header-settings">
          {/* <button className="btn btn-print">Print</button> */}
          <button
            className="btn btn-edit"
            onClick={() => setShowEditModal(true)}
          >
            Edit
          </button>
          <button
            className="btn btn-cancel"
            onClick={() => setShowDeleteModal(true)}
          >
            Cancel
          </button>
        </div>
      </div>
      <div className="events-content">
        <div className="view-event">
          <div className="event-card">
            <img
              src={
                event?.image || window.getPublicUrl("/assets/images/card-5.svg")
              } 
              className="event-img"
              alt="Event"
            />
            <div className="card-body">
              <div className="event-details-formatted">
                <div className="event-detail-row">
                  <span className="event-detail-label">Name of Event:</span>
                  <span className="event-detail-value">{event?.title || "-"}</span>
                </div>
                <div className="event-detail-row">
                  <span className="event-detail-label">Event Date:</span>
                  <span className="event-detail-value">
                    {event?.start_date ? (
                      event?.end_date && event.end_date !== event.start_date ? (
                        `${formatDateShort(event.start_date)} - ${formatDateShort(event.end_date)}`
                      ) : (
                        formatDateShort(event.start_date)
                      )
                    ) : "-"}
                  </span>
                </div>
                <div className="event-detail-row">
                  <span className="event-detail-label">Time:</span>
                  <span className="event-detail-value">{formatTime12Hour(event?.event_time) || "-"}</span>
                </div>
                {event?.venue && (
                  <div className="event-detail-row">
                    <span className="event-detail-label">Venue:</span>
                    <span className="event-detail-value">{event.venue}</span>
                  </div>
                )}
                <div className="event-detail-row">
                  <span className="event-detail-label">Location:</span>
                  <span className="event-detail-value">
                    {event?.state && event?.location 
                      ? `${event.location}, ${event.state}` 
                      : event?.location || event?.state || "-"}
                  </span>
                </div>
                {event?.document && (
                  <div className="event-detail-row">
                    <span className="event-detail-label">View Add'l Details:</span>
                    <span className="event-detail-value">
                      <a
                        href={event.document}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1890ff", textDecoration: "underline", wordBreak: "break-all" }}
                      >
                        {event.document}
                      </a>
                    </span>
                  </div>
                )}
                <SpecialInstructionsDisplay text={event?.description} compact={false} />
                {event?.Rsvp_deadline && (
                  <div className="event-detail-row">
                    <span className="event-detail-label">RSVP Deadline:</span>
                    <span className="event-detail-value">{formatDateShort(event?.Rsvp_deadline)}</span>
                  </div>
                )}
                {event?.document && (
                  <div className="event-detail-row">
                    <span className="event-detail-label">Event Document:</span>
                    <span className="event-detail-value">
                      <a
                        href={event.document}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1890ff", textDecoration: "underline" }}
                      >
                        View
                      </a>
                    </span>
                  </div>
                )}
                <div className="event-detail-row event-invitation-status-section">
                  <div className="event-invitation-status-title">
                    {isFuneralEvent(event) ? "Notice" : "Invitation Status"}
                  </div>
                  <div className="event-invitation-status-counts">
                    {isFuneralEvent(event) ? (
                      <>
                        <strong>{total_invited != null ? total_invited : invited_members?.length || 0}</strong> Notice(s) Sent
                      </>
                    ) : (
                      <>
                        <strong>{total_invited != null ? total_invited : invited_members?.length || 0}</strong> Invited<span className="event-invitation-status-divider">|</span><strong>{accepted_count ?? 0}</strong> Accepted<span className="event-invitation-status-divider">|</span><strong>{declined_count ?? 0}</strong> Declined<span className="event-invitation-status-divider">|</span><strong>{pending_count ?? 0}</strong> Pending
                      </>
                    )}
                  </div>
                </div>
                {event?.status === 2 && (
                  <p
                    className="event-cancelled-label"
                    style={{
                      color: "#dc3545",
                      fontWeight: "bold",
                      marginTop: "6px",
                      marginBottom: "4px",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    Cancelled Event
                  </p>
                )}
              </div>
              <div className="view-icon">
                {/* <div className="pb-3"><span><img src="/assets/images/eye-icon.svg" /> </span></div>
                      <div onClick={() => openAddEditModal("Edit", userEvent)} className=""><span><img src="/assets/images/edit-icon.svg" /></span></div> */}
                {/* <div className="pb-3 cursor-pointer">
                  <span className="icon-btn">
                    <img src="/assets/images/eye-icon.svg" alt="View" />
                  </span>
                </div>
                <div className="cursor-pointer">
                  <span className="icon-btn">
                    <img src="/assets/images/edit-icon.svg" alt="Edit" />
                  </span>
                </div> */}
              </div>
            </div>
          </div>

          <div className="customer-tablle-main">
            <table className="">
              <thead>
                <tr>
                  <th
                    onClick={toggleNameSortOrder}
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      padding: "4px 12px 4px 24px",
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <span>Name</span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#025FEB",
                          lineHeight: "1",
                        }}
                      >
                        {nameSortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    </span>
                  </th>
                  <th>Family Line Color</th>
                  <th>RSVP Deadline</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedInvitedMembers && sortedInvitedMembers.length > 0 ? (
                  sortedInvitedMembers.map((member) => (
                    <tr key={member.invitation_id}>
                      <td style={{ padding: "4px 12px 4px 24px" }}>
                        {formatPersonName(member)}
                      </td>
                      <td>
                        {renderNameWithColor(
                          getHeadOfLineName(member) || "Start/Top of Line",
                          member?.hex_code || "#000000",
                          member?.color_name ? `${member.color_name} Line` : ""
                        )}
                      </td>
                      <td>
                        {member?.Rsvp_deadline
                          ? formatDateShort(member.Rsvp_deadline)
                          : event?.Rsvp_deadline
                          ? formatDateShort(event.Rsvp_deadline)
                          : "-"}
                      </td>
                      <td>{renderStatusBadge(member.invitation_status)} </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      No members invited yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showEditModal}
        onRequestClose={() => setShowEditModal(false)}
        contentLabel="Notification Modal"
        overlayClassName="popup-overlay invite-overlay"
        portalClassName="event-moodal popup-card addmember-card"
        ariaHideApp={false}
      >
        <CreateEventModal
          isOpen={showEditModal}
          eventDetails={{
            ...event,
            Event_Invitations: invited_members || [],
            invited_members: invited_members || [],
          }}
          editMode={"Edit"}
          family_tree_id={family_tree_id}
          onClose={handleEditModalClose}
        />
      </Modal>

      {/*Delete Modal */}
      <DeleteEventModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        eventId={event?.id}
      />
    </div>
  );
};

export default EventDetails;
